// Lightweight WebSocket client with reconnect.
//
// Reconnect rules:
//   - On connection drop, retry with exponential backoff (1s..15s).
//   - If we have a session token in localStorage, send `rejoin` on every
//     successful (re)connect — the server will reattach the player to their
//     seat and replay the latest view.
//   - Caller subscribes to incoming messages via `onMessage`.
//
// Visibility / wake handling:
//   Mobile browsers freeze JS and silently kill backgrounded WS connections.
//   The naive "reconnect on every visibilitychange" we shipped earlier turned
//   out to be a flicker storm — alt-tabbing on desktop would force-close a
//   perfectly healthy socket, the host would see the player drop and re-join,
//   and Sviat reported "constantly disconnects, duplicates". The current
//   policy:
//     * Track the moment the tab last went hidden.
//     * On visible:
//         - If the socket isn't OPEN, kick a reconnect (something already
//           broke — don't wait for backoff).
//         - If the tab was hidden for less than the zombie threshold, leave
//           the OPEN socket alone (routine alt-tab on desktop).
//         - Otherwise force-close — the OS may have silently killed the TCP
//           connection while readyState still reads OPEN. The server-side
//           heartbeat (30s ping/pong) is the other backstop.
//     * `pageshow` with `persisted=true` is BFCache restore (Safari back/fwd)
//       — always treat as a hard suspend and rebuild the socket.
//
// Anti-flicker invariants:
//   - openSocket() bails if a previous socket is still CONNECTING/OPEN, so a
//     visibility tick can't stack a second WebSocket on top of an in-flight
//     one (the server's `rejoin` would otherwise evict the legit socket via
//     `kicked` → dropSession() → soft-logout flicker).
//   - scheduleReconnect() debounces — calling it twice in quick succession
//     only fires one timeout.

import type { ClientMsg, ServerMsg } from '../../shared/src/types.js';
import { storageGet, storageRemove, storageSet } from './safeStorage.js';

const STORAGE_KEY = 'mafia.session.v1';
// A "real suspend" looks like the tab being hidden long enough that the
// mobile OS / carrier could have torn down the TCP socket without telling
// us. Anything shorter is treated as a benign alt-tab and the existing
// connection is reused. 25s sits below the server's 30s heartbeat so we
// can preemptively rebuild before the heartbeat would have caught the
// zombie — but well above typical alt-tab durations.
const ZOMBIE_HIDDEN_MS = 25_000;

export interface SessionInfo {
  token: string;
  playerId: string;
  isHost: boolean;
  roomCode: string;
}

type Listener = (msg: ServerMsg) => void;
type ConnListener = (state: 'connecting' | 'open' | 'closed') => void;

export interface GameSocketOptions {
  // If false, the socket keeps its session token in memory only (no
  // localStorage read/write). Used by the dev console where we need many
  // simultaneous sessions in one tab without them stomping on each other.
  persistSession?: boolean;
}

export class GameSocket {
  private ws: WebSocket | null = null;
  private url: string;
  private listeners = new Set<Listener>();
  private connListeners = new Set<ConnListener>();
  private reconnectAttempt = 0;
  private intentionalClose = false;
  private queue: ClientMsg[] = [];
  // Buffer the *next* rejoin attempt so we don't fire two when the server
  // hasn't ack'd the first.
  private pendingRejoin = false;
  private persist: boolean;
  // In-memory session for non-persistent sockets (dev console).
  private memSession: SessionInfo | null = null;
  // Set when the server kicks us with reason 'displaced' (the same session
  // was opened in another tab, which now owns the token). While latched we
  // keep the stored session but never auto-rejoin with it — rejoining would
  // displace the new tab right back and the two tabs would fight for the
  // seat forever. Cleared when a fresh `session` arrives (the user joined a
  // room from this tab, or deliberately reclaimed the seat via reload —
  // a reload constructs a new GameSocket, which starts unlatched).
  private displaced = false;
  // Visibility-aware reconnect bookkeeping. See header comment.
  private lastHiddenAt = 0;
  private boundVisibility: (() => void) | null = null;
  private boundPageShow: ((e: PageTransitionEvent) => void) | null = null;
  // Dedupe scheduled reconnects so a burst of close/visibility events
  // doesn't queue several openSocket() timers in parallel.
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(opts: GameSocketOptions = {}) {
    const proto = location.protocol === 'https:' ? 'wss' : 'ws';
    this.url = `${proto}://${location.host}/ws`;
    this.persist = opts.persistSession !== false;
  }

  private readSession(): SessionInfo | null {
    return this.persist ? loadSession() : this.memSession;
  }
  private writeSession(info: SessionInfo) {
    if (this.persist) saveSession(info);
    else this.memSession = info;
  }
  private dropSession() {
    if (this.persist) clearSession();
    else this.memSession = null;
  }

  connect() {
    this.intentionalClose = false;
    this.openSocket();
    if (typeof document === 'undefined' || !this.persist) return;
    if (this.boundVisibility) return; // already wired

    if (document.visibilityState === 'hidden') this.lastHiddenAt = Date.now();

    this.boundVisibility = () => {
      if (this.intentionalClose) return;
      if (document.visibilityState === 'hidden') {
        this.lastHiddenAt = Date.now();
        return;
      }
      // visible
      const ws = this.ws;
      const hiddenMs = this.lastHiddenAt ? Date.now() - this.lastHiddenAt : 0;
      this.lastHiddenAt = 0;
      if (!ws || ws.readyState !== WebSocket.OPEN) {
        // Something already broke while we were hidden — don't wait for the
        // exponential backoff to finish.
        this.reconnectAttempt = 0;
        this.openSocket();
        return;
      }
      // Routine alt-tab — leave the socket alone. Closing here was the bug
      // that caused the "constantly disconnects" report.
      if (hiddenMs < ZOMBIE_HIDDEN_MS) return;
      // Long hide → suspect zombie TCP. Close to rebuild; the close handler
      // will schedule a reconnect with backoff and the new socket will
      // rejoin via session token.
      try { ws.close(); } catch { /* close event will follow */ }
    };
    document.addEventListener('visibilitychange', this.boundVisibility);

    this.boundPageShow = (e: PageTransitionEvent) => {
      if (this.intentionalClose) return;
      // Only BFCache restore — `persisted=false` is a normal load, where
      // the constructor already kicked openSocket().
      if (!e.persisted) return;
      const ws = this.ws;
      if (ws && ws.readyState === WebSocket.OPEN) {
        try { ws.close(); } catch { /* same */ }
      } else {
        this.reconnectAttempt = 0;
        this.openSocket();
      }
    };
    window.addEventListener('pageshow', this.boundPageShow);
  }

  private openSocket() {
    // Don't stack sockets: if a previous attempt is still alive (CONNECTING
    // or OPEN) bail out — otherwise the second `rejoin` would evict the
    // first via `kicked`, dropSession() would fire on the just-evicted
    // tab, and the user would be soft-logged-out for no reason. Only
    // proceed when the current socket is null or fully CLOSED.
    if (this.ws && this.ws.readyState !== WebSocket.CLOSED) return;
    this.notifyConn('connecting');
    const ws = new WebSocket(this.url);
    this.ws = ws;
    ws.addEventListener('open', () => {
      this.reconnectAttempt = 0;
      this.notifyConn('open');
      // If we have a session, the server requires `rejoin` before any
      // session-bound message — otherwise queued user actions (vote, action,
      // etc.) racing with reconnect get rejected as NO_SESSION. Send rejoin
      // first and hold the queue until the server acks with 'session'.
      // Without a session, drain immediately so host:create / player:join
      // (which created the queue) goes through. A displaced socket also
      // connects idle: its stored token belongs to the OTHER tab now (see
      // the `displaced` field).
      const sess = this.readSession();
      if (sess && !this.displaced) {
        this.pendingRejoin = true;
        ws.send(JSON.stringify({ kind: 'rejoin', sessionToken: sess.token }));
      } else {
        this.drainQueue();
      }
    });
    ws.addEventListener('message', (ev) => {
      let msg: ServerMsg;
      try {
        msg = JSON.parse(ev.data);
      } catch {
        return;
      }
      // Persist session token whenever the server hands one back, then drain
      // any messages that were queued while we waited for the rejoin ack.
      if (msg.kind === 'session') {
        this.writeSession({
          token: msg.token,
          playerId: msg.playerId,
          isHost: msg.isHost,
          roomCode: msg.roomCode,
        });
        // A fresh session means this tab owns a seat again — re-enable
        // auto-rejoin if a displacement had latched it off.
        this.displaced = false;
        this.pendingRejoin = false;
        this.drainQueue();
      }
      // If the server says NO_SESSION, drop our stale token so we don't
      // spam-rejoin on every reconnect. Drop any queued user actions too —
      // they're addressed to a session that no longer exists, and draining
      // them would just produce more "Connect first" errors. The host/player
      // will need to re-create / re-join (App.tsx routes them back home on
      // the matching `error` payload).
      // EXCEPT while displaced (audit P1-12 follow-up): the stored token
      // belongs to the OTHER tab. This idle socket can still earn a
      // NO_SESSION (e.g. a queued/stray session-bound message), and clearing
      // the SHARED localStorage key here would erase the live tab's session
      // through the side door.
      if (msg.kind === 'error' && msg.code === 'NO_SESSION') {
        if (!this.displaced) this.dropSession();
        this.pendingRejoin = false;
        this.queue = [];
      }
      if (msg.kind === 'kicked') {
        if (msg.reason === 'displaced') {
          // Another tab opened the same session; the server attached IT and
          // kicked US — but the token was NOT revoked, the new tab owns it.
          // Clearing the shared localStorage key here erased the live tab's
          // session out from under it and soft-locked its next reload
          // (audit P1-12). Keep the token; just stop auto-rejoining.
          this.displaced = true;
        } else {
          // Real kick / disband: the server revoked the token. Terminal for
          // this session — forget it so we don't auto-rejoin.
          this.dropSession();
        }
        // The server then closes us; reset the backoff so the follow-up
        // reconnect is a clean, immediate idle connect (ready for a re-join)
        // rather than a delayed, banner-flickering one.
        this.reconnectAttempt = 0;
      }
      for (const l of this.listeners) l(msg);
    });
    ws.addEventListener('close', () => {
      // Guard: a previous ws that we already replaced (via the visibility
      // force-close path) can still emit `close` later. Only honor it if
      // it matches the currently-tracked socket.
      if (this.ws !== ws) return;
      this.notifyConn('closed');
      this.ws = null;
      this.pendingRejoin = false;
      if (!this.intentionalClose) this.scheduleReconnect();
    });
    ws.addEventListener('error', () => {
      // 'close' will follow.
    });
  }

  private scheduleReconnect() {
    if (this.reconnectTimer != null) return; // already scheduled
    const delay = Math.min(15000, 1000 * Math.pow(2, this.reconnectAttempt++));
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.openSocket();
    }, delay);
  }

  send(msg: ClientMsg) {
    // While a rejoin is in flight, hold even non-session-bound messages: the
    // server is about to rebind the connection and reordering 'rejoin' after
    // user actions is the bug we're guarding against.
    if (this.ws && this.ws.readyState === WebSocket.OPEN && !this.pendingRejoin) {
      this.ws.send(JSON.stringify(msg));
    } else {
      this.queue.push(msg);
    }
  }

  private drainQueue() {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    const drained = this.queue;
    this.queue = [];
    for (const msg of drained) this.ws.send(JSON.stringify(msg));
  }

  onMessage(listener: Listener) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  onConnState(listener: ConnListener) {
    this.connListeners.add(listener);
    return () => this.connListeners.delete(listener);
  }

  private notifyConn(s: 'connecting' | 'open' | 'closed') {
    for (const l of this.connListeners) l(s);
  }

  forgetSession() {
    this.dropSession();
  }

  // Cleanly close the underlying socket; used by the dev console when tearing
  // down a simulated room. Suppresses reconnect and unhooks the global
  // visibility listeners so a torn-down instance can't ressurect itself.
  disconnect() {
    this.intentionalClose = true;
    if (this.reconnectTimer != null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    try { this.ws?.close(); } catch { /* ignore */ }
    this.ws = null;
    if (this.boundVisibility && typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.boundVisibility);
      this.boundVisibility = null;
    }
    if (this.boundPageShow && typeof window !== 'undefined') {
      window.removeEventListener('pageshow', this.boundPageShow);
      this.boundPageShow = null;
    }
  }

  getSession(): SessionInfo | null {
    return this.readSession();
  }
}

export function loadSession(): SessionInfo | null {
  const raw = storageGet(STORAGE_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as SessionInfo;
  } catch {
    return null;
  }
}
function saveSession(info: SessionInfo) {
  storageSet(STORAGE_KEY, JSON.stringify(info));
}
function clearSession() {
  storageRemove(STORAGE_KEY);
}
